import { computed, DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  getState,
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { ApiErrorStatusResponse, UserDto, UserRegistrationDto } from '@workspace/data-types';
import { environment } from '@workspace/ng-config';
import { ApiService } from '@workspace/ng-http';
import { LoginResponse } from 'angular-auth-oidc-client';
import { CookieService } from 'ngx-cookie-service';
import { catchError, EMPTY, map, Observable, tap } from 'rxjs';
import { SessionStorageKey } from '../../models/storage.enum';
import { ApplicationStore } from '../application/application.store';
import { UserRegistrationStore } from '../user-registration/userRegistration.store';
import { UserStore } from '../user/user.store';
import { withStoreDevtools } from '../with-devtools';

export interface OpenIdUserData {
  sub: string;
  email_verified: boolean;
  name: string;
  preferred_username: string;
  given_name: string;
  family_name: string;
  email: string;
}

export interface AuthState {
  authenticated: boolean;
  initialized: boolean;
  error?: ApiErrorStatusResponse | null;
  urlRedirect?: string;
  token?: string;
  loading: boolean;
  isImpersonated: boolean;
  openIdUserData: OpenIdUserData | undefined;
  authenticatedUser?: UserDto;
  authenticatedUserRegistration?: UserRegistrationDto;
}

const initialState: AuthState = {
  authenticated: false,
  initialized: false,
  loading: false,
  isImpersonated: false,
  error: null,
  urlRedirect: undefined,
  token: undefined,
  openIdUserData: undefined,
  authenticatedUser: undefined,
  authenticatedUserRegistration: undefined,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withStoreDevtools('auth'),
  withState(initialState),

  withMethods((store) => {
    const COOKIE_DOMAIN = `.${environment.domain}`;
    const api = inject(ApiService);
    const userStore = inject(UserStore);
    const userRegistrationStore = inject(UserRegistrationStore);
    const destroyRef = inject(DestroyRef);
    const cookieService = inject(CookieService);

    return {
      initAuth(openIdConnect: LoginResponse, urlRedirect?: string): Observable<void> {
        patchState(store, {
          initialized: false,
          error: undefined,
          urlRedirect,
        });
        return this.loadCurrentUser(openIdConnect, urlRedirect).pipe(
          catchError(() => {
            this.failedAuth();
            return EMPTY;
          }),
          map((): void => void 0),
        );
      },

      closeImpersonate(): Observable<void> {
        return api
          .userSessions()
          .revokeImpersonate()
          .pipe(
            takeUntilDestroyed(destroyRef),
            tap(() => {
              cookieService.delete(SessionStorageKey.IMPERSONATE_USER_ID, '/', COOKIE_DOMAIN);
              window.location.reload();
            }),
          );
      },

      initAuthRegistration(urlRedirect?: string): Observable<void> {
        patchState(store, {
          initialized: false,
          error: undefined,
          urlRedirect,
        });
        return this.loadCurrentUserRegistration().pipe(
          catchError(() => {
            this.failedAuth();
            return EMPTY;
          }),
          map((): void => void 0),
        );
      },

      loadCurrentUser(openIdConnect: LoginResponse, urlRedirect?: string): Observable<UserDto> {
        return api
          .user()
          .me()
          .pipe(
            takeUntilDestroyed(destroyRef),
            tap((user) => {
              userStore.updateCurrentUser(user);

              patchState(store, {
                openIdUserData: openIdConnect.userData,
                isImpersonated: cookieService.get(SessionStorageKey.IMPERSONATE_USER_ID) ? true : false,
                authenticatedUser: user,
                authenticated: true,
                loading: false,
                initialized: true,
                urlRedirect,
              });
            }),
            map((user) => user),
            catchError(() => {
              this.failedAuth();
              return EMPTY;
            }),
          );
      },

      loadCurrentUserRegistration(): Observable<UserRegistrationDto> {
        return api
          .auth()
          .userRegistration()
          .me()
          .pipe(
            tap((userRegistration) => {
              userRegistrationStore.updateCurrentUserRegistration(userRegistration);

              patchState(store, {
                authenticatedUserRegistration: userRegistration,
                authenticated: true,
                loading: false,
                initialized: true,
              });
            }),
            catchError(() => {
              this.failedAuth();
              return EMPTY;
            }),
          );
      },

      failedAuth() {
        patchState(store, {
          authenticated: false,
          initialized: true,
          loading: false,
        });
      },

      logout() {
        userStore.removeCurrentUser();
        userRegistrationStore.removeCurrentUserRegistration();
        patchState(store, {
          authenticated: false,
          loading: false,
          error: null,
          authenticatedUser: undefined,
          authenticatedUserRegistration: undefined,
        });
      },
    };
  }),

  withComputed((store) => ({
    isAuthenticated: computed(() => store.authenticated()),
    isUserAuthenticated: computed(() => store.authenticated() && store.authenticatedUser?.()),
    isUserRegistrationAuthenticated: computed(
      () => store.authenticated() && store.authenticatedUserRegistration?.(),
    ),
    isInitialized: computed(() => store.initialized()),
    isLoading: computed(() => store.loading()),
    hasError: computed(() => store.error?.()),
    currentAuthenticatedUser: computed(() => store.authenticatedUser?.()),
    currentAuthenticatedUserRegistration: computed(() => store.authenticatedUserRegistration?.()),
  })),
  withHooks((store) => {
    const applicationStore = inject(ApplicationStore);

    return {
      onInit() {
        effect(() => {
          if (applicationStore.debug()) {
            console.debug('Auth state:', getState(store));
          }
        });
      },
    };
  }),
);

export type AuthStore = InstanceType<typeof AuthStore>;
